import React, { Suspense, lazy, useCallback, useEffect, useRef, useState } from 'react'
import { HashRouter, Navigate, Outlet, Route, Routes } from 'react-router-dom'
import 'sweetalert2/dist/sweetalert2.min.css'
import '@mepro/styles/mepro-swal.css'
import '@mepro/styles/mepro-bet-confirm.css'
import {
  getDefaultTheme,
  getThemeById,
  parseHtGoldThemeFromTemplate,
} from './config/themes'
import { applyHtGoldCssVars, DEFAULT_HT_GOLD_THEME_ID, HT_GOLD_THEMES } from './lib/htGoldTheme'
import type { ColorScheme, TenantThemeConfig } from './types/theme'
import { COLOR_SCHEMES } from './types/theme'
import { getTenantContent } from '@mepro/services/tenantService'
import { lotteryService } from '@mepro/services/lotteryService'
import {
  getRuntimeTenantIdSync,
  resolveRuntimeTenantId,
  setRuntimeTenantId,
} from '@mepro/lib/runtimeTenant'
import { getApiBaseUrl } from '@mepro/lib/apiBaseUrl'
import { parseLiveTvChannels, parseHuayYoutubeLive } from '@mepro/lib/liveTv'
import { useSmartWebSocket } from '@mepro/hooks/useSmartWebSocket'
import { useAnimateOnScroll } from '@mepro/hooks/useAnimateOnScroll'
import usePopupManagerOptimized from '@mepro/hooks/usePopupManagerOptimized'
import ScrollToTop from '@mepro/components/ScrollToTop'
import * as authService from '@mepro/services/authService'
import PopupModal from '@mepro/components/PopupModal'
import { ModernAnnouncementAlert } from '@mepro/components/ModernAnnouncementAlert'
import ModernMaintenancePage from '@mepro/pages/ModernMaintenancePage'
import {
  fetchMaintenanceStatusCached,
  isInternalStagingHostname,
  type MaintenanceState,
} from '@huay/shared/tenantManagementPublicClient'
import HtGoldLayout, { type AssetConfig } from './components/HtGoldLayout'
import HtGoldMeproSurface from './components/HtGoldMeproSurface'
import HtGoldLobbyHuayNav from './components/HtGoldLobbyHuayNav'
import RequireAuth from './components/RequireAuth'
import HomePage from './pages/HomePage'
import {
  LineBridgeHashRedirect,
  MaintenanceWarningOverlay,
  type MaintenanceWarning,
} from './components/HtGoldProductionOverlays'
import { updateSocialMetaTags } from './utils/socialMeta'
import {
  MEEDUANG_BRAND_PATHS,
  isMeeduangHost,
  resolveMeeduangFaviconUrl,
  resolveMeeduangLogoUrl,
} from './lib/meeduangBranding'

const MAINTENANCE_CHECK_INTERVAL = 30_000

async function fetchMaintenanceStatus(tenantId?: string): Promise<MaintenanceState> {
  return fetchMaintenanceStatusCached(getApiBaseUrl(), tenantId)
}

function sanitizeBrandingAssetUrl(url?: string | null): string {
  const raw = String(url || '').trim()
  if (!raw) return ''
  try {
    const base = typeof window !== 'undefined' ? window.location.origin : 'https://example.com'
    const currentProtocol = typeof window !== 'undefined' ? window.location.protocol : 'https:'
    const currentHost = typeof window !== 'undefined' ? window.location.hostname : ''
    const resolved = new URL(raw, base)
    const isLocalDevHost = ['localhost', '127.0.0.1', '0.0.0.0'].includes(resolved.hostname)
    const isHttpOnHttpsPage = currentProtocol === 'https:' && resolved.protocol === 'http:'
    const isLocalhostLeak = isLocalDevHost && (!import.meta.env.DEV || resolved.hostname !== currentHost)
    if (isLocalhostLeak) return ''
    if (isHttpOnHttpsPage) return ''
    return resolved.href
  } catch {
    return raw.startsWith('/') ? raw : ''
  }
}

import './styles/ht-gold-app.css'
import './styles/ht-gold-lobby.css'
import './styles/ht-gold-mepro-bridge.css'
import './styles/ht-gold-swal.css'

const STORAGE_THEME_KEY = 'huay-ht-gold-theme'
/** Dev-only preview switcher. Production themes come from tenant `frontendTemplate`. */
const SHOW_THEME_SWITCHER =
  import.meta.env.DEV && import.meta.env.VITE_SHOW_THEME_SWITCHER !== 'false'
const DEFAULT_CDN_URL = 'https://huay-cdn-assets-prod.s3.ap-southeast-1.amazonaws.com'
const TENANT_CONTENT_TIMEOUT_MS = 8000

const ModernTopupPage = lazy(() => import('@mepro/pages/ModernTopupPage'))
const ModernTopupConfirmPage = lazy(() => import('@mepro/pages/ModernTopupConfirmPage'))
const ModernTopupSlipPage = lazy(() => import('@mepro/pages/ModernTopupSlipPage'))
const ModernDepositTimeoutPage = lazy(() => import('@mepro/pages/ModernDepositTimeoutPage'))
const ModernWithdrawPage = lazy(() => import('@mepro/pages/ModernWithdrawPage'))
const ModernLobbyHuayListAllPage = lazy(() => import('@mepro/pages/lobby-huay/ModernLobbyHuayListAllPage'))
const ModernLobbyHuayBetPage = lazy(() => import('@mepro/pages/lobby-huay/ModernLobbyHuayBetPage'))
const ModernLobbyHuayLotteryTermsPage = lazy(() => import('@mepro/pages/lobby-huay/ModernLobbyHuayLotteryTermsPage'))
const ModernLobbyHuayYeekeePage = lazy(() => import('@mepro/pages/lobby-huay/ModernLobbyHuayYeekeePage'))
const ModernLobbyHuayYeekeeResultPage = lazy(() => import('@mepro/pages/lobby-huay/ModernLobbyHuayYeekeeResultPage'))
const ModernLobbyHuaySetLottoGamePage = lazy(() => import('@mepro/pages/lobby-huay/ModernLobbyHuaySetLottoGamePage'))
const ModernLobbyHuaySlipPage = lazy(() => import('@mepro/pages/lobby-huay/ModernLobbyHuaySlipPage'))
const ModernLobbyHuaySlipDetailPage = lazy(() => import('@mepro/pages/lobby-huay/ModernLobbyHuaySlipDetailPage'))
const HtGoldResultPage = lazy(() => import('./pages/HtGoldResultPage'))
const ModernLobbyHuaySetPage = lazy(() => import('@mepro/pages/lobby-huay/ModernLobbyHuaySetPage'))
const ModernLobbyHuaySetEditPage = lazy(() => import('@mepro/pages/lobby-huay/ModernLobbyHuaySetEditPage'))
const ModernLobbyHuayWinNumberPage = lazy(() => import('@mepro/pages/lobby-huay/ModernLobbyHuayWinNumberPage'))
const ModernRegisterPage = lazy(() => import('@mepro/pages/ModernRegisterPage'))
const ModernRegisterCompletePage = lazy(() => import('@mepro/pages/ModernRegisterCompletePage'))
const ModernPasscodePage = lazy(() => import('@mepro/pages/ModernPasscodePage'))
const HtGoldDashboardPage = lazy(() => import('./pages/HtGoldDashboardPage'))
const ModernLobbyCasinoPage = lazy(() => import('@mepro/pages/ModernLobbyCasinoPage'))
const ModernPromotionPage = lazy(() => import('@mepro/pages/ModernPromotionPage'))
const ModernRewardLayout = lazy(() => import('@mepro/pages/reward/ModernRewardLayout'))
const RewardCashbackPage = lazy(() => import('@mepro/pages/reward/RewardCashbackPage'))
const RewardLossPage = lazy(() => import('@mepro/pages/reward/RewardLossPage'))
const RewardReferPage = lazy(() => import('@mepro/pages/reward/RewardReferPage'))
const RewardRankPage = lazy(() => import('@mepro/pages/reward/RewardRankPage'))
const ModernHowtoPage = lazy(() => import('@mepro/pages/ModernHowtoPage'))
const ModernContactPage = lazy(() => import('@mepro/pages/ModernContactPage'))
const ModernCashbackPage = lazy(() => import('@mepro/pages/ModernCashbackPage'))
const ModernMemberPage = lazy(() => import('@mepro/pages/ModernMemberPage'))
const ModernLiveTvPage = lazy(() => import('@mepro/pages/ModernLiveTvPage'))
const ModernHistoryLayout = lazy(() => import('@mepro/pages/history/ModernHistoryLayout'))
const HistoryDepositPage = lazy(() => import('@mepro/pages/history/HistoryDepositPage'))
const HistoryWithdrawPage = lazy(() => import('@mepro/pages/history/HistoryWithdrawPage'))
const HistoryCreditPage = lazy(() => import('@mepro/pages/history/HistoryCreditPage'))
const HistoryGamblePage = lazy(() => import('@mepro/pages/history/HistoryGamblePage'))
const HistoryPendingPage = lazy(() => import('@mepro/pages/history/HistoryPendingPage'))
const HistoryBtdPage = lazy(() => import('@mepro/pages/history/HistoryBtdPage'))
const HistoryGamesLayout = lazy(() => import('@mepro/pages/history/HistoryGamesLayout'))
const HistoryGamesIndexRedirect = lazy(() =>
  import('@mepro/pages/history/HistoryGamesLayout').then((m) => ({ default: m.HistoryGamesIndexRedirect })),
)
const HistoryRedEnvelopePage = lazy(() => import('@mepro/pages/history/HistoryRedEnvelopePage'))
const HistoryLuckyWheelPage = lazy(() => import('@mepro/pages/history/HistoryLuckyWheelPage'))
const HistoryBerThongPage = lazy(() => import('@mepro/pages/history/HistoryBerThongPage'))
const ModernAffiliateLayout = lazy(() => import('@mepro/pages/affiliate/ModernAffiliateLayout'))
const AffiliateOverviewPage = lazy(() => import('@mepro/pages/affiliate/AffiliateOverviewPage'))
const AffiliateMemberPage = lazy(() => import('@mepro/pages/affiliate/AffiliateMemberPage'))
const AffiliateIncomePage = lazy(() => import('@mepro/pages/affiliate/AffiliateIncomePage'))
const AffiliateToptenPage = lazy(() => import('@mepro/pages/affiliate/AffiliateToptenPage'))
const AffiliateWithdrawalPage = lazy(() => import('@mepro/pages/affiliate/AffiliateWithdrawalPage'))
const ModernPointsLayout = lazy(() => import('@mepro/pages/points/ModernPointsLayout'))
const PointsMoneytopointPage = lazy(() => import('./pages/points/PointsMoneytopointPage'))
const PointsPointtomoneyPage = lazy(() => import('./pages/points/PointsPointtomoneyPage'))
const PointsRewardsPage = lazy(() => import('@mepro/pages/points/PointsRewardsPage'))
const PointsRewardHistoryPage = lazy(() => import('@mepro/pages/points/PointsRewardHistoryPage'))
const PredictionPage = lazy(() => import('@mepro/pages/points/PredictionPage'))
const RedEnvelopeGamePage = lazy(() => import('@mepro/pages/points/RedEnvelopeGamePage'))
const RedEnvelopeDemoPage = lazy(() => import('@mepro/pages/points/RedEnvelopeDemoPage'))
const RedEnvelopeHistoryPage = lazy(() => import('@mepro/pages/points/RedEnvelopeHistoryPage'))
const LuckyWheelGamePage = lazy(() => import('@mepro/pages/points/LuckyWheelGamePage'))
const BerThongLobbyPage = lazy(() => import('@mepro/pages/points/BerThongLobbyPage'))
const BerThongSheetsPage = lazy(() => import('@mepro/pages/points/BerThongSheetsPage'))
const BerThongGamePage = lazy(() => import('@mepro/pages/points/BerThongGamePage'))

const getAssetConfig = (): AssetConfig => ({
  CDN_BASE_URL:
    import.meta.env.VITE_CDN_URL ||
    (typeof window !== 'undefined'
      ? (window as Window & { CDN_BASE_URL?: string }).CDN_BASE_URL
      : undefined) ||
    DEFAULT_CDN_URL,
  CSS_PATH: '/css',
  IMG_PATH: '/img',
  FONTS_PATH: '/fonts',
})

function mapToColorScheme(themeId: string): ColorScheme {
  const tokens = HT_GOLD_THEMES[themeId] || HT_GOLD_THEMES[DEFAULT_HT_GOLD_THEME_ID]
  return {
    ...COLOR_SCHEMES['today-modern-blue'],
    primary: tokens.primary,
    secondary: tokens.secondary,
    accent: tokens.primaryLight,
    background: 'transparent',
    text: '#ffffff',
    border: tokens.primary,
    warning: tokens.primary,
  }
}

function parsePaymentRatesContent(
  raw?: { type?: string; textContent?: string; imageUrl?: string } | null
): TenantThemeConfig['settings']['paymentRates'] {
  if (!raw || typeof raw !== 'object') return undefined
  const type = raw.type === 'image' ? 'image' : 'text'
  const textContent = String(raw.textContent || '')
  const imageUrl = String(raw.imageUrl || '').trim()
  if (!textContent && !imageUrl) return undefined
  return { type, textContent, imageUrl }
}

function buildThemeConfig(
  tenantId: string,
  themeId: string,
  tenantContentData: Awaited<ReturnType<typeof getTenantContent>>['data'] | null
): TenantThemeConfig {
  const meta = getThemeById(themeId) || getDefaultTheme()
  const colorScheme = mapToColorScheme(meta.id)
  const assets = getAssetConfig()
  const assetBase = `${assets.CDN_BASE_URL}/${meta.folder}`
  const siteTitle = tenantContentData?.content?.siteTitle?.trim()
  const tenantName = tenantContentData?.name?.trim()
  const titleBrand = siteTitle?.split('|')[0]?.trim()
  // Prefer site title / brand over internal tenant display names
  const siteName =
    (titleBrand && !/อันดับ|ที่\s*1|number\s*1/i.test(titleBrand) ? titleBrand : null) ||
    (tenantName && !/^lenhuay$/i.test(tenantName) ? tenantName : null) ||
    'HuayToday'

  const features = tenantContentData?.features as
    | {
        depositEnabled?: boolean
        withdrawEnabled?: boolean
        lotteryEnabled?: boolean
        casinoEnabled?: boolean
        lineLoginEnabled?: boolean
        webChatEnabled?: boolean
        smsOtpEnabled?: boolean
        sessionIdleMinutes?: number
        wipWupMinutes?: number
        wipWupMinutesOther?: number
        liveTvEnabled?: boolean
        predictionEnabled?: boolean
        redEnvelopeEnabled?: boolean
        luckyWheelEnabled?: boolean
        berThongEnabled?: boolean
        liveTvChannels?: unknown
        huayYoutubeLive?: unknown
        liveTvPlayerStartMuted?: boolean
      }
    | undefined
  const wipWupMinutes =
    typeof features?.wipWupMinutes === 'number' &&
    features.wipWupMinutes >= 1 &&
    features.wipWupMinutes <= 120
      ? features.wipWupMinutes
      : 15
  const wipWupMinutesOther =
    typeof features?.wipWupMinutesOther === 'number' &&
    features.wipWupMinutesOther >= 1 &&
    features.wipWupMinutesOther <= 120
      ? features.wipWupMinutesOther
      : 15
  const sessionIdleMinutes =
    typeof features?.sessionIdleMinutes === 'number' &&
    features.sessionIdleMinutes >= 0 &&
    features.sessionIdleMinutes <= 120
      ? features.sessionIdleMinutes
      : 30
  const paymentRates = parsePaymentRatesContent(tenantContentData?.content?.paymentRates)
  const logoUrl = resolveMeeduangLogoUrl(
    sanitizeBrandingAssetUrl(tenantContentData?.branding?.logo)
  )
  const faviconUrl = resolveMeeduangFaviconUrl(
    sanitizeBrandingAssetUrl(tenantContentData?.branding?.favicon),
    `${assetBase}${meta.assets.favicon}`
  )

  return {
    tenantId,
    themeId: meta.id,
    theme: {
      baseTheme: 'modern',
      colorScheme,
      branding: {
        siteName,
        logoUrl,
        faviconUrl,
      },
    },
    settings: {
      marqueeText:
        tenantContentData?.content?.marqueeText || `ยินดีต้อนรับเข้าสู่ ${siteName}`,
      contactInfo: tenantContentData?.content?.contactInfo,
      depositEnabled: features?.depositEnabled !== false,
      withdrawEnabled: features?.withdrawEnabled !== false,
      lotteryEnabled: features?.lotteryEnabled !== false,
      casinoEnabled: features?.casinoEnabled !== false,
      lineLoginEnabled: features?.lineLoginEnabled !== false,
      smsOtpEnabled: features?.smsOtpEnabled === true,
      ...(typeof features?.webChatEnabled === 'boolean'
        ? { webChatEnabled: features.webChatEnabled }
        : {}),
      wipWupMinutes,
      wipWupMinutesOther,
      sessionIdleMinutes,
      liveTvChannels: parseLiveTvChannels(features?.liveTvChannels) as TenantThemeConfig['settings']['liveTvChannels'],
      liveTvEnabled: features?.liveTvEnabled === true,
      predictionEnabled: features?.predictionEnabled === true,
      redEnvelopeEnabled: features?.redEnvelopeEnabled === true,
      luckyWheelEnabled: features?.luckyWheelEnabled === true,
      berThongEnabled: features?.berThongEnabled === true,
      huayYoutubeLive: parseHuayYoutubeLive(features?.huayYoutubeLive) as TenantThemeConfig['settings']['huayYoutubeLive'],
      liveTvPlayerStartMuted: features?.liveTvPlayerStartMuted !== false,
      ...(paymentRates ? { paymentRates } : {}),
    },
  }
}

/** Non-blocking CDN CSS — never stall the app on S3. */
function loadLayoutCss(folder: string, styles: string[]) {
  const base = `${getAssetConfig().CDN_BASE_URL}/${folder}`
  for (let i = 0; i < styles.length; i++) {
    const href = base + styles[i]
    const id = `htg-cdn-css-${i}`
    const existing = document.querySelector(`link[data-css-id="${id}"]`) as HTMLLinkElement | null
    if (existing?.href === href) continue
    existing?.remove()
    const link = document.createElement('link')
    link.rel = 'stylesheet'
    link.href = href
    link.setAttribute('data-css-id', id)
    document.head.appendChild(link)
  }
}

function applyBridgeCssVars(colorScheme: ColorScheme) {
  document.querySelectorAll('style[data-htg-bridge]').forEach((el) => el.remove())
  const style = document.createElement('style')
  style.setAttribute('data-htg-bridge', 'true')
  style.textContent = `
    :root {
      --color-primary: ${colorScheme.primary};
      --color-secondary: ${colorScheme.secondary};
      --color-accent: ${colorScheme.accent};
      --htd-primary: ${colorScheme.primary};
      --htd-secondary: ${colorScheme.secondary};
      --htd-accent: ${colorScheme.accent};
      --htd-blue: ${colorScheme.primary};
      --htd-navy: ${colorScheme.secondary};
    }
  `
  document.head.appendChild(style)
}

function upsertHeadLink(rel: string, href: string, attrs: Record<string, string> = {}) {
  const selector = Object.entries(attrs).reduce(
    (sel, [key, value]) => `${sel}[${key}="${value}"]`,
    `link[rel="${rel}"]`
  )
  let link = document.head.querySelector(selector) as HTMLLinkElement | null
  if (!link) {
    link = document.createElement('link')
    link.rel = rel
    for (const [key, value] of Object.entries(attrs)) {
      link.setAttribute(key, value)
    }
    document.head.appendChild(link)
  }
  link.href = href
  return link
}

function updateFavicon(url: string, appleTouchUrl?: string) {
  if (!url.trim()) return
  const clean = url.trim()
  const pathForType = clean.split('?')[0].toLowerCase()
  const type = pathForType.endsWith('.svg')
    ? 'image/svg+xml'
    : pathForType.endsWith('.png')
      ? 'image/png'
      : pathForType.endsWith('.webp')
        ? 'image/webp'
        : 'image/x-icon'
  // Stable cache-bust (avoid encoding path fragments into ?v=)
  const busted = clean.includes('?') ? clean : `${clean}?v=1`
  const icon = upsertHeadLink('icon', busted)
  icon.type = type
  icon.setAttribute('sizes', 'any')

  const shortcut = upsertHeadLink('shortcut icon', busted)
  shortcut.type = type

  const touchSrc = (appleTouchUrl || clean).trim()
  if (touchSrc) {
    const touch = upsertHeadLink('apple-touch-icon', touchSrc.includes('?') ? touchSrc : `${touchSrc}?v=1`)
    touch.removeAttribute('type')
  }
}

function applyThemeVisuals(config: TenantThemeConfig) {
  applyHtGoldCssVars(config.themeId)
  applyBridgeCssVars(config.theme.colorScheme)
  const meta = getThemeById(config.themeId) || getDefaultTheme()
  const assetBase = `${getAssetConfig().CDN_BASE_URL}/${meta.folder}`
  loadLayoutCss(meta.folder, meta.assets.styles)

  const siteName = config.theme.branding.siteName || 'HT Gold'
  document.title = siteName

  const faviconUrl = resolveMeeduangFaviconUrl(
    config.theme.branding.faviconUrl,
    `${assetBase}${meta.assets.favicon}`
  )
  const logoUrl =
    resolveMeeduangLogoUrl(config.theme.branding.logoUrl) ||
    config.theme.branding.logoUrl?.trim() ||
    `${assetBase}${meta.assets.logo}`
  const socialImageUrl =
    isMeeduangHost() && logoUrl.includes('logo-wordmark')
      ? MEEDUANG_BRAND_PATHS.logoPng
      : logoUrl
  try {
    updateFavicon(faviconUrl, logoUrl)
  } catch {
    // ignore favicon failure
  }

  updateSocialMetaTags({
    title: siteName,
    description: config.settings?.marqueeText || siteName,
    imageUrl: socialImageUrl,
    url: window.location.origin,
  })
}

async function withTimeout<T>(promise: Promise<T>, ms: number, label: string): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined
  try {
    return await Promise.race([
      promise,
      new Promise<T>((_, reject) => {
        timer = setTimeout(() => reject(new Error(`${label} timed out after ${ms}ms`)), ms)
      }),
    ])
  } finally {
    if (timer) clearTimeout(timer)
  }
}

const PageFallback = () => (
  <div style={{ padding: '1.5rem', textAlign: 'center', color: 'var(--htg-text-muted)' }}>
    กำลังโหลดหน้า…
  </div>
)

/** Lobby outlet: HT Gold tab strip + mepro page content. */
const LobbyOutlet: React.FC<{ themeConfig: TenantThemeConfig; assetConfig: AssetConfig }> = ({
  themeConfig,
  assetConfig,
}) => (
  <>
    <HtGoldLobbyHuayNav />
    <Outlet context={{ themeConfig, assetConfig }} />
  </>
)

const App: React.FC = () => {
  const [themeConfig, setThemeConfig] = useState<TenantThemeConfig | null>(null)
  const [themeId, setThemeId] = useState(DEFAULT_HT_GOLD_THEME_ID)
  const [tenantId, setTenantId] = useState('')
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [maintenance, setMaintenance] = useState<MaintenanceState>({ enabled: false })
  const [maintenanceChecked, setMaintenanceChecked] = useState(() => isInternalStagingHostname())
  const [maintWarn, setMaintWarn] = useState<MaintenanceWarning>({
    active: false,
    countdownMinutes: 0,
    secondsLeft: 0,
  })
  const maintWarnTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const [viewAsHandling, setViewAsHandling] = useState(() => {
    if (typeof window === 'undefined') return false
    return authService.getViewAsTokenFromHash() !== null
  })
  const [viewAsError, setViewAsError] = useState<string | null>(null)
  const assetConfig = getAssetConfig()

  const loadTenant = useCallback(async () => {
    setIsLoading(true)
    setError(null)

    const syncTid =
      getRuntimeTenantIdSync() ||
      import.meta.env.VITE_TENANT_ID ||
      'huaytoday'

    const bootConfig = buildThemeConfig(syncTid, getDefaultTheme().id, null)
    setTenantId(syncTid)
    setThemeId(bootConfig.themeId)
    setThemeConfig(bootConfig)
    applyThemeVisuals(bootConfig)
    setRuntimeTenantId(syncTid)
    lotteryService.setTenantId(syncTid)
    setIsLoading(false)

    try {
      const tid =
        (await withTimeout(resolveRuntimeTenantId(), 5000, 'resolveRuntimeTenantId')) || syncTid
      setRuntimeTenantId(tid)
      setTenantId(tid)
      lotteryService.setTenantId(tid)

      const contentRes = await withTimeout(
        getTenantContent(tid),
        TENANT_CONTENT_TIMEOUT_MS,
        'getTenantContent'
      )
      const contentData = contentRes?.success ? contentRes.data : null

      const fromAdmin = parseHtGoldThemeFromTemplate(contentData?.frontendTemplate)
      const saved = SHOW_THEME_SWITCHER
        ? parseHtGoldThemeFromTemplate(localStorage.getItem(STORAGE_THEME_KEY))
        : null
      const preferred = fromAdmin || saved || getDefaultTheme().id
      if (fromAdmin && !SHOW_THEME_SWITCHER) {
        localStorage.removeItem(STORAGE_THEME_KEY)
      }
      const config = buildThemeConfig(tid, preferred, contentData)
      setThemeId(config.themeId)
      setThemeConfig(config)
      applyThemeVisuals(config)
    } catch (err) {
      console.warn('[user-ht-gold] tenant enrich failed (using boot config)', err)
      setError(err instanceof Error ? err.message : 'โหลด tenant content ไม่ครบ — ใช้ค่าเริ่มต้น')
    }
  }, [])

  useEffect(() => {
    void loadTenant()
  }, [loadTenant])

  useEffect(() => {
    let active = true
    let prevEnabled = false

    const check = async (tid?: string) => {
      if (isInternalStagingHostname()) {
        if (!active) return
        setMaintenance({ enabled: false })
        setMaintenanceChecked(true)
        return
      }
      if (!tid) return
      try {
        const s = await fetchMaintenanceStatus(tid)
        if (!active) return
        if (s.enabled && !prevEnabled) {
          void Promise.resolve(authService.logout()).catch(() => {})
        }
        prevEnabled = s.enabled
        setMaintenance(s)
      } finally {
        if (active) setMaintenanceChecked(true)
      }
    }

    if (tenantId) setMaintenanceChecked(false)
    check(tenantId || getRuntimeTenantIdSync() || undefined)
    const id = setInterval(() => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return
      check(tenantId || getRuntimeTenantIdSync() || undefined)
    }, MAINTENANCE_CHECK_INTERVAL)
    return () => {
      active = false
      clearInterval(id)
    }
  }, [tenantId])

  useEffect(() => {
    if (!tenantId) return
    const handleMaintenanceModeChanged = (event: Event) => {
      if (isInternalStagingHostname()) return
      const data = (event as CustomEvent<MaintenanceState & { tenantId?: string }>).detail
      if (data?.tenantId && data.tenantId !== tenantId) return
      setMaintenance({
        enabled: data.enabled === true,
        reason: data.reason,
        estimatedEnd: data.estimatedEnd,
      })
      setMaintWarn({ active: false, countdownMinutes: 0, secondsLeft: 0 })
      if (maintWarnTimerRef.current) {
        clearInterval(maintWarnTimerRef.current)
        maintWarnTimerRef.current = null
      }
      if (data.enabled) void Promise.resolve(authService.logout()).catch(() => {})
    }
    const handleMaintenanceWarning = (event: Event) => {
      if (isInternalStagingHostname()) return
      const data = (event as CustomEvent<MaintenanceWarning & { tenantId?: string }>).detail
      if (data?.tenantId && data.tenantId !== tenantId) return
      const totalSeconds = (data.countdownMinutes || 5) * 60
      setMaintWarn({
        active: true,
        reason: data.reason,
        estimatedEnd: data.estimatedEnd,
        maintenanceStart: data.maintenanceStart,
        countdownMinutes: data.countdownMinutes || 5,
        secondsLeft: totalSeconds,
      })
      if (maintWarnTimerRef.current) clearInterval(maintWarnTimerRef.current)
      maintWarnTimerRef.current = setInterval(() => {
        setMaintWarn((prev) => {
          if (!prev.active) return prev
          const next = prev.secondsLeft - 1
          if (next <= 0) {
            if (maintWarnTimerRef.current) {
              clearInterval(maintWarnTimerRef.current)
              maintWarnTimerRef.current = null
            }
            return { ...prev, secondsLeft: 0 }
          }
          return { ...prev, secondsLeft: next }
        })
      }, 1000)
    }
    window.addEventListener('maintenance_mode_changed', handleMaintenanceModeChanged as EventListener)
    window.addEventListener('maintenance_warning', handleMaintenanceWarning as EventListener)
    return () => {
      window.removeEventListener('maintenance_mode_changed', handleMaintenanceModeChanged as EventListener)
      window.removeEventListener('maintenance_warning', handleMaintenanceWarning as EventListener)
      if (maintWarnTimerRef.current) {
        clearInterval(maintWarnTimerRef.current)
        maintWarnTimerRef.current = null
      }
    }
  }, [tenantId])

  useEffect(() => {
    if (isLoading) return
    const token = authService.getViewAsTokenFromHash()
    if (!token) {
      setViewAsHandling(false)
      return
    }
    setViewAsError(null)
    authService.applyViewAsToken(token).then((result) => {
      if (result.ok) {
        window.location.hash = '#/dashboard'
      } else {
        setViewAsError(result.error || 'ไม่สามารถเข้า View As ได้')
      }
      setViewAsHandling(false)
    })
  }, [isLoading])

  const handleContentUpdate = useCallback(
    (newContent: unknown) => {
      if (typeof newContent !== 'object' || newContent === null) return
      const c = newContent as Record<string, unknown>
      if (c._refreshTrigger) {
        void loadTenant()
        return
      }
      if (c.type === 'huay_type_updated') {
        window.dispatchEvent(
          new CustomEvent('huay_type_updated', {
            detail: {
              huayType: c.huayType,
              code: c.code,
              isActive: c.isActive,
              name: c.name,
              timestamp: c.timestamp,
            },
          })
        )
      }
      if (
        (c.type === 'lottery_round_updated' || c.type === 'lottery_round_update') &&
        (c.round || c.data)
      ) {
        window.dispatchEvent(
          new CustomEvent('lottery_round_updated', {
            detail: {
              round: (c.round ?? c.data) as Record<string, unknown>,
              sourceType: c.type,
            },
          })
        )
      }

      const content = c.content as Record<string, unknown> | undefined
      const branding = c.branding as { logo?: string; favicon?: string } | undefined
      const features = c.features as Record<string, unknown> | undefined
      const nextSiteTitle = (content?.siteTitle as string | undefined)?.trim()
      const nextMarquee =
        (content?.marqueeText as string | undefined) ?? (c.marqueeText as string | undefined)
      const nextContact =
        (content?.contactInfo as TenantThemeConfig['settings']['contactInfo'] | undefined) ??
        (c.contactInfo as TenantThemeConfig['settings']['contactInfo'] | undefined)
      const nextLogoUrl = sanitizeBrandingAssetUrl(branding?.logo)
      const nextFaviconUrl = sanitizeBrandingAssetUrl(branding?.favicon)

      const bool = (key: string) =>
        typeof features?.[key] === 'boolean' ? (features[key] as boolean) : undefined
      const nextWebChat = bool('webChatEnabled')
      const nextDepositEnabled = bool('depositEnabled')
      const nextWithdrawEnabled = bool('withdrawEnabled')
      const nextLotteryEnabled = bool('lotteryEnabled')
      const nextCasinoEnabled = bool('casinoEnabled')
      const nextLineLoginEnabled = bool('lineLoginEnabled')
      const nextSmsOtpEnabled = bool('smsOtpEnabled')
      const nextLiveTvEnabled = bool('liveTvEnabled')
      const nextPredictionEnabled = bool('predictionEnabled')
      const nextRedEnvelopeEnabled = bool('redEnvelopeEnabled')
      const nextLuckyWheelEnabled = bool('luckyWheelEnabled')
      const nextBerThongEnabled = bool('berThongEnabled')
      const nextLiveTvPlayerStartMuted = bool('liveTvPlayerStartMuted')
      const nextWipWup =
        typeof features?.wipWupMinutes === 'number' &&
        (features.wipWupMinutes as number) >= 1 &&
        (features.wipWupMinutes as number) <= 120
          ? (features.wipWupMinutes as number)
          : undefined
      const nextWipWupOther =
        typeof features?.wipWupMinutesOther === 'number' &&
        (features.wipWupMinutesOther as number) >= 1 &&
        (features.wipWupMinutesOther as number) <= 120
          ? (features.wipWupMinutesOther as number)
          : undefined
      const nextSessionIdle =
        typeof features?.sessionIdleMinutes === 'number' &&
        (features.sessionIdleMinutes as number) >= 0 &&
        (features.sessionIdleMinutes as number) <= 120
          ? (features.sessionIdleMinutes as number)
          : undefined
      const nextLiveTvChannels =
        features?.liveTvChannels !== undefined
          ? (parseLiveTvChannels(features.liveTvChannels) as TenantThemeConfig['settings']['liveTvChannels'])
          : undefined
      const nextHuayYoutubeLive =
        features?.huayYoutubeLive !== undefined
          ? (parseHuayYoutubeLive(features.huayYoutubeLive) as TenantThemeConfig['settings']['huayYoutubeLive'])
          : undefined
      const nextPaymentRates =
        content?.paymentRates !== undefined
          ? parsePaymentRatesContent(
              content.paymentRates as { type?: string; textContent?: string; imageUrl?: string }
            )
          : undefined

      if (nextSiteTitle) document.title = nextSiteTitle

      setThemeConfig((prev) => {
        if (!prev) return null
        const nextSettings = {
          ...prev.settings,
          ...(nextMarquee !== undefined && { marqueeText: nextMarquee }),
          ...(nextContact !== undefined && { contactInfo: nextContact }),
          ...(nextWebChat !== undefined && { webChatEnabled: nextWebChat }),
          ...(nextWipWup !== undefined && { wipWupMinutes: nextWipWup }),
          ...(nextWipWupOther !== undefined && { wipWupMinutesOther: nextWipWupOther }),
          ...(nextDepositEnabled !== undefined && { depositEnabled: nextDepositEnabled }),
          ...(nextWithdrawEnabled !== undefined && { withdrawEnabled: nextWithdrawEnabled }),
          ...(nextLotteryEnabled !== undefined && { lotteryEnabled: nextLotteryEnabled }),
          ...(nextCasinoEnabled !== undefined && { casinoEnabled: nextCasinoEnabled }),
          ...(nextSessionIdle !== undefined && { sessionIdleMinutes: nextSessionIdle }),
          ...(nextLineLoginEnabled !== undefined && { lineLoginEnabled: nextLineLoginEnabled }),
          ...(nextSmsOtpEnabled !== undefined && { smsOtpEnabled: nextSmsOtpEnabled }),
          ...(nextLiveTvEnabled !== undefined && { liveTvEnabled: nextLiveTvEnabled }),
          ...(nextPredictionEnabled !== undefined && { predictionEnabled: nextPredictionEnabled }),
          ...(nextRedEnvelopeEnabled !== undefined && {
            redEnvelopeEnabled: nextRedEnvelopeEnabled,
          }),
          ...(nextLuckyWheelEnabled !== undefined && { luckyWheelEnabled: nextLuckyWheelEnabled }),
          ...(nextBerThongEnabled !== undefined && { berThongEnabled: nextBerThongEnabled }),
          ...(nextLiveTvChannels !== undefined && { liveTvChannels: nextLiveTvChannels }),
          ...(nextHuayYoutubeLive !== undefined && { huayYoutubeLive: nextHuayYoutubeLive }),
          ...(nextLiveTvPlayerStartMuted !== undefined && {
            liveTvPlayerStartMuted: nextLiveTvPlayerStartMuted,
          }),
          ...(nextPaymentRates !== undefined && { paymentRates: nextPaymentRates }),
        }
        let nextBranding = { ...prev.theme.branding }
        if (nextSiteTitle !== undefined) nextBranding = { ...nextBranding, siteName: nextSiteTitle }
        if (nextLogoUrl) nextBranding = { ...nextBranding, logoUrl: nextLogoUrl }
        if (nextFaviconUrl) nextBranding = { ...nextBranding, faviconUrl: nextFaviconUrl }
        return {
          ...prev,
          theme: { ...prev.theme, branding: nextBranding },
          settings: nextSettings,
        }
      })

      if (nextFaviconUrl) {
        updateFavicon(nextFaviconUrl, nextLogoUrl || undefined)
      }
      if (nextSiteTitle || nextLogoUrl) {
        updateSocialMetaTags({
          title: nextSiteTitle || document.title,
          description: nextMarquee || document.title,
          imageUrl: nextLogoUrl,
          url: window.location.origin,
        })
      }
    },
    [loadTenant]
  )

  useSmartWebSocket({
    tenantId,
    onContentUpdate: handleContentUpdate,
    enabled: !!tenantId && !!themeConfig,
  })

  useAnimateOnScroll()

  const { currentPopup, dismissPopup } = usePopupManagerOptimized({
    tenantId,
    route: '/',
    enabled: Boolean(tenantId),
  })

  const switchTheme = useCallback(
    (nextId: string) => {
      if (!themeConfig) return
      const normalized = parseHtGoldThemeFromTemplate(nextId) || DEFAULT_HT_GOLD_THEME_ID
      const next = buildThemeConfig(themeConfig.tenantId, normalized, {
        tenantId: themeConfig.tenantId,
        name: themeConfig.theme.branding.siteName,
        content: {
          marqueeText: themeConfig.settings.marqueeText,
          contactInfo: themeConfig.settings.contactInfo,
          paymentRates: themeConfig.settings.paymentRates,
        },
        branding: {
          logo: themeConfig.theme.branding.logoUrl,
          favicon: themeConfig.theme.branding.faviconUrl,
        },
        features: {
          depositEnabled: themeConfig.settings.depositEnabled,
          withdrawEnabled: themeConfig.settings.withdrawEnabled,
          lotteryEnabled: themeConfig.settings.lotteryEnabled,
          casinoEnabled: themeConfig.settings.casinoEnabled,
          lineLoginEnabled: themeConfig.settings.lineLoginEnabled,
          smsOtpEnabled: themeConfig.settings.smsOtpEnabled,
          webChatEnabled: themeConfig.settings.webChatEnabled,
          sessionIdleMinutes: themeConfig.settings.sessionIdleMinutes,
          wipWupMinutes: themeConfig.settings.wipWupMinutes,
          wipWupMinutesOther: themeConfig.settings.wipWupMinutesOther,
          liveTvEnabled: themeConfig.settings.liveTvEnabled,
          predictionEnabled: themeConfig.settings.predictionEnabled,
          redEnvelopeEnabled: themeConfig.settings.redEnvelopeEnabled,
          luckyWheelEnabled: themeConfig.settings.luckyWheelEnabled,
          berThongEnabled: themeConfig.settings.berThongEnabled,
          liveTvChannels: themeConfig.settings.liveTvChannels,
          huayYoutubeLive: themeConfig.settings.huayYoutubeLive,
          liveTvPlayerStartMuted: themeConfig.settings.liveTvPlayerStartMuted,
        },
      })
      if (SHOW_THEME_SWITCHER) {
        localStorage.setItem(STORAGE_THEME_KEY, normalized)
      }
      setThemeId(normalized)
      setThemeConfig(next)
      applyThemeVisuals(next)
    },
    [themeConfig]
  )

  if (!maintenanceChecked && !isInternalStagingHostname()) {
    return (
      <div className="htg-shell" style={{ paddingTop: '3rem', textAlign: 'center' }}>
        <p>กำลังตรวจสอบสถานะระบบ…</p>
      </div>
    )
  }

  if (maintenance.enabled) {
    return (
      <ModernMaintenancePage
        reason={maintenance.reason}
        estimatedEnd={maintenance.estimatedEnd}
        services={maintenance.services}
        logoUrl={themeConfig?.theme?.branding?.logoUrl}
        siteName={themeConfig?.theme?.branding?.siteName}
      />
    )
  }

  if (viewAsHandling) {
    return (
      <div className="htg-shell" style={{ paddingTop: '3rem', textAlign: 'center' }}>
        <p>กำลังเข้า View As…</p>
      </div>
    )
  }

  if (viewAsError) {
    return (
      <div className="htg-shell" style={{ paddingTop: '3rem', textAlign: 'center' }}>
        <p style={{ color: '#fbbf24' }}>{viewAsError}</p>
        <a href="#/">กลับหน้าหลัก</a>
      </div>
    )
  }

  if (isLoading || !themeConfig) {
    return (
      <div className="htg-shell" style={{ paddingTop: '3rem', textAlign: 'center' }}>
        <p>กำลังโหลด…</p>
      </div>
    )
  }

  return (
    <HashRouter>
      <ScrollToTop />
      <LineBridgeHashRedirect />
      {maintWarn.active ? <MaintenanceWarningOverlay warning={maintWarn} /> : null}
      {tenantId ? <ModernAnnouncementAlert tenantId={tenantId} key="announcement" /> : null}
      <Routes>
        <Route
          element={
            <HtGoldLayout
              themeConfig={themeConfig}
              assetConfig={assetConfig}
              themeId={themeId}
              onSwitchTheme={switchTheme}
              showThemeSwitcher={SHOW_THEME_SWITCHER}
            />
          }
        >
          <Route index element={<HomePage themeConfig={themeConfig} assetConfig={assetConfig} />} />
          <Route path="login" element={<HomePage themeConfig={themeConfig} assetConfig={assetConfig} />} />
          <Route
            path="passcode"
            element={
              <HtGoldMeproSurface>
                <Suspense fallback={<PageFallback />}>
                  <ModernPasscodePage themeConfig={themeConfig} />
                </Suspense>
              </HtGoldMeproSurface>
            }
          />
          <Route
            path="register"
            element={
              <HtGoldMeproSurface>
                <Suspense fallback={<PageFallback />}>
                  <ModernRegisterPage themeConfig={themeConfig} />
                </Suspense>
              </HtGoldMeproSurface>
            }
          />
          <Route
            path="register/complete"
            element={
              <HtGoldMeproSurface>
                <Suspense fallback={<PageFallback />}>
                  <ModernRegisterCompletePage themeConfig={themeConfig} />
                </Suspense>
              </HtGoldMeproSurface>
            }
          />
          <Route
            path="contact"
            element={
              <HtGoldMeproSurface>
                <Suspense fallback={<PageFallback />}>
                  <ModernContactPage themeConfig={themeConfig} assetConfig={assetConfig} />
                </Suspense>
              </HtGoldMeproSurface>
            }
          />

          <Route
            path="lobby-huay"
            element={
              <RequireAuth>
                <HtGoldMeproSurface>
                  <LobbyOutlet themeConfig={themeConfig} assetConfig={assetConfig} />
                </HtGoldMeproSurface>
              </RequireAuth>
            }
          >
            <Route index element={<Navigate to="listall" replace />} />
            <Route path="listall" element={<Suspense fallback={<PageFallback />}><ModernLobbyHuayListAllPage /></Suspense>} />
            <Route path="lottery-terms" element={<Suspense fallback={<PageFallback />}><ModernLobbyHuayLotteryTermsPage /></Suspense>} />
            <Route path="bet" element={<Suspense fallback={<PageFallback />}><ModernLobbyHuayBetPage /></Suspense>} />
            <Route path="set-lotto-game" element={<Suspense fallback={<PageFallback />}><ModernLobbyHuaySetLottoGamePage /></Suspense>} />
            <Route path="yeekee" element={<Suspense fallback={<PageFallback />}><ModernLobbyHuayYeekeePage /></Suspense>} />
            <Route path="yeekee/result" element={<Suspense fallback={<PageFallback />}><ModernLobbyHuayYeekeeResultPage /></Suspense>} />
            <Route path="yeekee/bet" element={<Suspense fallback={<PageFallback />}><ModernLobbyHuayBetPage /></Suspense>} />
            <Route path="slip" element={<Suspense fallback={<PageFallback />}><ModernLobbyHuaySlipPage /></Suspense>} />
            <Route path="slip/detail/:slipId" element={<Suspense fallback={<PageFallback />}><ModernLobbyHuaySlipDetailPage /></Suspense>} />
            <Route path="result" element={<Suspense fallback={<PageFallback />}><HtGoldResultPage /></Suspense>} />
            <Route path="set" element={<Suspense fallback={<PageFallback />}><ModernLobbyHuaySetPage /></Suspense>} />
            <Route path="set/create" element={<Suspense fallback={<PageFallback />}><ModernLobbyHuaySetEditPage /></Suspense>} />
            <Route path="set/edit/:id" element={<Suspense fallback={<PageFallback />}><ModernLobbyHuaySetEditPage /></Suspense>} />
            <Route path="win-number" element={<Suspense fallback={<PageFallback />}><ModernLobbyHuayWinNumberPage /></Suspense>} />
          </Route>

          <Route path="dashboard" element={<RequireAuth><Suspense fallback={<PageFallback />}><HtGoldDashboardPage themeConfig={themeConfig} assetConfig={assetConfig} /></Suspense></RequireAuth>} />
          <Route path="lobby-casino" element={<RequireAuth><HtGoldMeproSurface><Suspense fallback={<PageFallback />}><ModernLobbyCasinoPage themeConfig={themeConfig} assetConfig={assetConfig} /></Suspense></HtGoldMeproSurface></RequireAuth>} />
          <Route path="promotion" element={<RequireAuth><HtGoldMeproSurface><Suspense fallback={<PageFallback />}><ModernPromotionPage themeConfig={themeConfig} assetConfig={assetConfig} /></Suspense></HtGoldMeproSurface></RequireAuth>} />
          <Route path="reward" element={<RequireAuth><HtGoldMeproSurface><Suspense fallback={<PageFallback />}><ModernRewardLayout themeConfig={themeConfig} assetConfig={assetConfig} /></Suspense></HtGoldMeproSurface></RequireAuth>}>
            <Route index element={<Navigate to="cashback" replace />} />
            <Route path="cashback" element={<Suspense fallback={<PageFallback />}><RewardCashbackPage /></Suspense>} />
            <Route path="loss" element={<Suspense fallback={<PageFallback />}><RewardLossPage /></Suspense>} />
            <Route path="refer" element={<Suspense fallback={<PageFallback />}><RewardReferPage /></Suspense>} />
            <Route path="rank" element={<Suspense fallback={<PageFallback />}><RewardRankPage /></Suspense>} />
          </Route>
          <Route path="howto" element={<RequireAuth><HtGoldMeproSurface><Suspense fallback={<PageFallback />}><ModernHowtoPage themeConfig={themeConfig} assetConfig={assetConfig} /></Suspense></HtGoldMeproSurface></RequireAuth>} />
          <Route path="cashback" element={<RequireAuth><HtGoldMeproSurface><Suspense fallback={<PageFallback />}><ModernCashbackPage themeConfig={themeConfig} assetConfig={assetConfig} /></Suspense></HtGoldMeproSurface></RequireAuth>} />
          <Route path="member" element={<RequireAuth><HtGoldMeproSurface><Suspense fallback={<PageFallback />}><ModernMemberPage themeConfig={themeConfig} assetConfig={assetConfig} /></Suspense></HtGoldMeproSurface></RequireAuth>} />
          <Route path="live-tv" element={<RequireAuth><HtGoldMeproSurface><Suspense fallback={<PageFallback />}><ModernLiveTvPage themeConfig={themeConfig} assetConfig={assetConfig} /></Suspense></HtGoldMeproSurface></RequireAuth>} />
          <Route path="topup" element={<RequireAuth><HtGoldMeproSurface><Suspense fallback={<PageFallback />}><ModernTopupPage themeConfig={themeConfig} assetConfig={assetConfig} /></Suspense></HtGoldMeproSurface></RequireAuth>} />
          <Route path="topup/confirm" element={<RequireAuth><HtGoldMeproSurface><Suspense fallback={<PageFallback />}><ModernTopupConfirmPage themeConfig={themeConfig} assetConfig={assetConfig} /></Suspense></HtGoldMeproSurface></RequireAuth>} />
          <Route path="topup/slip" element={<RequireAuth><HtGoldMeproSurface><Suspense fallback={<PageFallback />}><ModernTopupSlipPage themeConfig={themeConfig} assetConfig={assetConfig} /></Suspense></HtGoldMeproSurface></RequireAuth>} />
          <Route path="topup/reserve" element={<RequireAuth><HtGoldMeproSurface><Suspense fallback={<PageFallback />}><ModernDepositTimeoutPage themeConfig={themeConfig} assetConfig={assetConfig} /></Suspense></HtGoldMeproSurface></RequireAuth>} />
          <Route path="withdraw" element={<RequireAuth><HtGoldMeproSurface><Suspense fallback={<PageFallback />}><ModernWithdrawPage themeConfig={themeConfig} assetConfig={assetConfig} /></Suspense></HtGoldMeproSurface></RequireAuth>} />
          <Route path="history" element={<RequireAuth><HtGoldMeproSurface><Suspense fallback={<PageFallback />}><ModernHistoryLayout themeConfig={themeConfig} assetConfig={assetConfig} /></Suspense></HtGoldMeproSurface></RequireAuth>}>
            <Route index element={<Navigate to="deposit" replace />} />
            <Route path="deposit" element={<Suspense fallback={<PageFallback />}><HistoryDepositPage /></Suspense>} />
            <Route path="withdraw" element={<Suspense fallback={<PageFallback />}><HistoryWithdrawPage /></Suspense>} />
            <Route path="credit" element={<Suspense fallback={<PageFallback />}><HistoryCreditPage /></Suspense>} />
            <Route path="gamble" element={<Suspense fallback={<PageFallback />}><HistoryGamblePage /></Suspense>} />
            <Route path="pending" element={<Suspense fallback={<PageFallback />}><HistoryPendingPage /></Suspense>} />
            <Route path="btd" element={<Suspense fallback={<PageFallback />}><HistoryBtdPage /></Suspense>} />
            <Route path="games" element={<Suspense fallback={<PageFallback />}><HistoryGamesLayout /></Suspense>}>
              <Route index element={<Suspense fallback={<PageFallback />}><HistoryGamesIndexRedirect /></Suspense>} />
              <Route path="red-envelope" element={<Suspense fallback={<PageFallback />}><HistoryRedEnvelopePage /></Suspense>} />
              <Route path="lucky-wheel" element={<Suspense fallback={<PageFallback />}><HistoryLuckyWheelPage /></Suspense>} />
              <Route path="ber-thong" element={<Suspense fallback={<PageFallback />}><HistoryBerThongPage /></Suspense>} />
            </Route>
          </Route>
          <Route path="affiliate" element={<RequireAuth><HtGoldMeproSurface><Suspense fallback={<PageFallback />}><ModernAffiliateLayout themeConfig={themeConfig} assetConfig={assetConfig} /></Suspense></HtGoldMeproSurface></RequireAuth>}>
            <Route index element={<Navigate to="overview" replace />} />
            <Route path="overview" element={<Suspense fallback={<PageFallback />}><AffiliateOverviewPage /></Suspense>} />
            <Route path="member" element={<Suspense fallback={<PageFallback />}><AffiliateMemberPage /></Suspense>} />
            <Route path="income" element={<Suspense fallback={<PageFallback />}><AffiliateIncomePage /></Suspense>} />
            <Route path="topten" element={<Suspense fallback={<PageFallback />}><AffiliateToptenPage /></Suspense>} />
            <Route path="withdrawal" element={<Suspense fallback={<PageFallback />}><AffiliateWithdrawalPage /></Suspense>} />
          </Route>
          <Route path="points" element={<RequireAuth><HtGoldMeproSurface><Suspense fallback={<PageFallback />}><ModernPointsLayout themeConfig={themeConfig} assetConfig={assetConfig} /></Suspense></HtGoldMeproSurface></RequireAuth>}>
            <Route index element={<Navigate to="moneytopoint" replace />} />
            <Route path="moneytopoint" element={<Suspense fallback={<PageFallback />}><PointsMoneytopointPage /></Suspense>} />
            <Route path="pointtomoney" element={<Suspense fallback={<PageFallback />}><PointsPointtomoneyPage /></Suspense>} />
            <Route path="rewards" element={<Suspense fallback={<PageFallback />}><PointsRewardsPage /></Suspense>} />
            <Route path="reward-history" element={<Suspense fallback={<PageFallback />}><PointsRewardHistoryPage /></Suspense>} />
            <Route path="games/prediction" element={<Suspense fallback={<PageFallback />}><PredictionPage /></Suspense>} />
            <Route path="games/red-envelope" element={<Suspense fallback={<PageFallback />}><RedEnvelopeGamePage /></Suspense>} />
            <Route path="games/red-envelope/history" element={<Suspense fallback={<PageFallback />}><RedEnvelopeHistoryPage /></Suspense>} />
            <Route path="games/red-envelope-demo" element={<Suspense fallback={<PageFallback />}><RedEnvelopeDemoPage /></Suspense>} />
            <Route path="games/lucky-wheel" element={<Suspense fallback={<PageFallback />}><LuckyWheelGamePage /></Suspense>} />
            <Route path="games/ber-thong" element={<Suspense fallback={<PageFallback />}><BerThongLobbyPage /></Suspense>} />
            <Route path="games/ber-thong/huay/:huayKey" element={<Suspense fallback={<PageFallback />}><BerThongSheetsPage /></Suspense>} />
            <Route path="games/ber-thong/sheet/:sheetId" element={<Suspense fallback={<PageFallback />}><BerThongGamePage /></Suspense>} />
          </Route>
          <Route path="lottery/prediction" element={<Navigate to="/points/games/prediction" replace />} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      {currentPopup && tenantId ? (
        <PopupModal
          key={currentPopup._id}
          popup={currentPopup}
          tenantId={tenantId}
          onClose={dismissPopup}
        />
      ) : null}
      {error && import.meta.env.DEV ? (
        <div
          style={{
            position: 'fixed',
            left: 8,
            bottom: 72,
            zIndex: 9999,
            maxWidth: 360,
            padding: '0.5rem 0.7rem',
            borderRadius: 10,
            background: 'rgba(0,0,0,0.75)',
            color: '#fecaca',
            fontSize: 12,
          }}
        >
          {error}
        </div>
      ) : null}
    </HashRouter>
  )
}

export default App
